/**
 * "Build Your Own Plan" pricing config + validation (#89).
 *
 * Single source of truth for plan tiers and priced components. Defined as a
 * typed const (rather than a separate `.json` asset) because `@axon/common`
 * is bundled with `.d.ts` generation, and tsup's dts pipeline cannot bundle a
 * JSON import. The data is still validated against a Zod schema at module
 * import time, so a malformed edit fails fast (at build/boot) instead of
 * producing silently-wrong quotes — the behaviour the spec asked for.
 */
import { z } from 'zod';

import type { PricingConfig } from './pricing.types';

const baseFeeConfigSchema = z.object({ price: z.number().nonnegative() });

const perUnitConfigSchema = z.object({
  unitPrice: z.number().nonnegative(),
  min: z.number().int().nonnegative(),
  max: z.number().int().positive(),
  step: z.number().int().positive(),
  included: z.number().int().nonnegative(),
});

const volumeTierSchema = z.object({
  min: z.number().int().positive(),
  max: z.number().int().positive(),
  unitPrice: z.number().nonnegative(),
});

const volumeTieredConfigSchema = z
  .object({
    included: z.number().int().nonnegative(),
    tiers: z.array(volumeTierSchema).min(1),
  })
  // Tiers must cover the billable range with no gaps: the first tier starts
  // right after `included`, every tier's min is the previous tier's max + 1,
  // and each tier's max >= min. A gap would silently make units free; an
  // overlap would double-bill. The engine treats the LAST tier as open-ended,
  // so its `max` is only an upper label, not a hard cap (no dropped units).
  .superRefine((cfg, ctx) => {
    const expectedFirstMin = cfg.included + 1;
    if (cfg.tiers[0].min !== expectedFirstMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `First tier min (${cfg.tiers[0].min}) must equal included + 1 (${expectedFirstMin}) — no gap or overlap allowed`,
        path: ['tiers', 0, 'min'],
      });
    }
    for (let i = 0; i < cfg.tiers.length; i++) {
      const tier = cfg.tiers[i];
      if (tier.max < tier.min) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Tier ${i} max (${tier.max}) must be >= min (${tier.min})`,
          path: ['tiers', i, 'max'],
        });
      }
      if (i > 0 && tier.min !== cfg.tiers[i - 1].max + 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Tier ${i} min (${tier.min}) must equal previous tier max + 1 (${cfg.tiers[i - 1].max + 1}) — tiers must be contiguous`,
          path: ['tiers', i, 'min'],
        });
      }
    }
  });

const toggleConfigSchema = z.object({ price: z.number().nonnegative() });

const toggleIntegrationConfigSchema = z.object({
  price: z.number().nonnegative(),
  provider: z.string().min(1),
});

const selectConfigSchema = z.object({
  options: z
    .array(
      z.object({
        value: z.string().min(1),
        label: z.string().min(1),
        price: z.number().nonnegative(),
      })
    )
    .min(1),
});

const categorySchema = z.enum(['base', 'users', 'volume', 'addon', 'integration', 'support']);
const componentBase = {
  slug: z.string().min(1),
  category: categorySchema,
  label: z.string().min(1),
  description: z.string().optional(),
};

const componentSchema = z.discriminatedUnion('type', [
  z.object({ ...componentBase, type: z.literal('base_fee'), config: baseFeeConfigSchema }),
  z.object({ ...componentBase, type: z.literal('per_unit'), config: perUnitConfigSchema }),
  z.object({
    ...componentBase,
    type: z.literal('volume_tiered'),
    config: volumeTieredConfigSchema,
  }),
  z.object({ ...componentBase, type: z.literal('toggle'), config: toggleConfigSchema }),
  z.object({
    ...componentBase,
    type: z.literal('toggle_integration'),
    config: toggleIntegrationConfigSchema,
  }),
  z.object({ ...componentBase, type: z.literal('select'), config: selectConfigSchema }),
]);

const selectionValueSchema = z.union([z.number(), z.boolean(), z.string()]);

const tierSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  highlighted: z.boolean().optional(),
  defaults: z.record(z.string(), selectionValueSchema),
});

export const pricingConfigSchema = z.object({
  version: z.string().min(1),
  currency: z.string().length(3),
  minimumMonthlyPrice: z.number().nonnegative(),
  annualDiscountPercent: z.number().min(0).max(100),
  tiers: z.array(tierSchema).min(1),
  components: z.array(componentSchema).min(1),
});

/** Raw config data — edit prices/tiers here. Validated by `pricingConfigSchema` below. */
const RAW_PRICING_CONFIG = {
  version: '1.0.0',
  currency: 'USD',
  minimumMonthlyPrice: 99,
  annualDiscountPercent: 10,
  tiers: [
    {
      slug: 'starter',
      name: 'pricing.tier.starter',
      description: 'pricing.tier.starter.desc',
      defaults: { users: 3, drivers: 10, shipments: 200 },
    },
    {
      slug: 'professional',
      name: 'pricing.tier.professional',
      description: 'pricing.tier.professional.desc',
      highlighted: true,
      defaults: {
        users: 15,
        drivers: 50,
        shipments: 2000,
        ai_pack: true,
        dat_integration: true,
        qbo_integration: true,
        support: 'business',
      },
    },
    {
      slug: 'enterprise',
      name: 'pricing.tier.enterprise',
      description: 'pricing.tier.enterprise.desc',
      defaults: {
        users: 50,
        drivers: 200,
        shipments: 10000,
        ai_pack: true,
        multi_leg: true,
        compliance_pack: true,
        support: 'priority',
      },
    },
  ],
  components: [
    {
      slug: 'base_platform',
      category: 'base',
      type: 'base_fee',
      label: 'pricing.component.base_platform',
      config: { price: 99 },
    },
    {
      slug: 'users',
      category: 'users',
      type: 'per_unit',
      label: 'pricing.component.users',
      description: 'pricing.component.users.desc',
      config: { unitPrice: 29, min: 1, max: 100, step: 1, included: 1 },
    },
    {
      slug: 'drivers',
      category: 'users',
      type: 'per_unit',
      label: 'pricing.component.drivers',
      description: 'pricing.component.drivers.desc',
      config: { unitPrice: 19, min: 0, max: 500, step: 5, included: 0 },
    },
    {
      slug: 'shipments',
      category: 'volume',
      type: 'volume_tiered',
      label: 'pricing.component.shipments',
      description: 'pricing.component.shipments.desc',
      config: {
        included: 100,
        tiers: [
          { min: 101, max: 1000, unitPrice: 2.5 },
          { min: 1001, max: 5000, unitPrice: 1.5 },
          { min: 5001, max: 999999, unitPrice: 0.75 },
        ],
      },
    },
    {
      slug: 'ai_pack',
      category: 'addon',
      type: 'toggle',
      label: 'pricing.component.ai_pack',
      description: 'pricing.component.ai_pack.desc',
      config: { price: 149 },
    },
    {
      slug: 'multi_leg',
      category: 'addon',
      type: 'toggle',
      label: 'pricing.component.multi_leg',
      description: 'pricing.component.multi_leg.desc',
      config: { price: 129 },
    },
    {
      slug: 'fleet_maintenance',
      category: 'addon',
      type: 'toggle',
      label: 'pricing.component.fleet_maintenance',
      description: 'pricing.component.fleet_maintenance.desc',
      config: { price: 69 },
    },
    {
      slug: 'compliance_pack',
      category: 'addon',
      type: 'toggle',
      label: 'pricing.component.compliance_pack',
      description: 'pricing.component.compliance_pack.desc',
      config: { price: 99 },
    },
    {
      slug: 'dat_integration',
      category: 'integration',
      type: 'toggle_integration',
      label: 'pricing.component.dat',
      config: { price: 59, provider: 'DAT' },
    },
    {
      slug: 'truckstop_integration',
      category: 'integration',
      type: 'toggle_integration',
      label: 'pricing.component.truckstop',
      config: { price: 59, provider: 'Truckstop' },
    },
    {
      slug: 'uber_freight_integration',
      category: 'integration',
      type: 'toggle_integration',
      label: 'pricing.component.uber_freight',
      config: { price: 59, provider: 'Uber Freight' },
    },
    {
      slug: 'qbo_integration',
      category: 'integration',
      type: 'toggle_integration',
      label: 'pricing.component.qbo',
      config: { price: 39, provider: 'QuickBooks' },
    },
    {
      slug: 'eld_integration',
      category: 'integration',
      type: 'toggle_integration',
      label: 'pricing.component.eld',
      config: { price: 39, provider: 'Motive ELD' },
    },
    {
      slug: 'factoring_integration',
      category: 'integration',
      type: 'toggle_integration',
      label: 'pricing.component.factoring',
      config: { price: 39, provider: 'TriumphPay' },
    },
    {
      slug: 'support',
      category: 'support',
      type: 'select',
      label: 'pricing.component.support',
      config: {
        options: [
          { value: 'community', label: 'pricing.support.community', price: 0 },
          { value: 'business', label: 'pricing.support.business', price: 99 },
          { value: 'priority', label: 'pricing.support.priority', price: 399 },
        ],
      },
    },
  ],
};

/**
 * The validated, typed pricing config. Throws at import if the data drifts from
 * the schema (e.g. a missing field or a bad component `type`).
 */
export const PRICING_CONFIG: PricingConfig = pricingConfigSchema.parse(
  RAW_PRICING_CONFIG
) as PricingConfig;
